import classNames from 'classnames';
import { PlusIcon } from 'tdesign-icons-react';
import { Button, Loading, MessagePlugin } from 'tdesign-react';

import { PIC_PRIVATE, PUBLIC } from '@/const/library';
import { LibraryObjectData, ResourceTabType, UserResourceType } from '@/interface/library';
import { libraryReducerActions } from '@/reducer/libraryListReducer';
import { CgiErrorCode } from '@/services/cgi';
import { deleteUserResourceCustomGroup, insertLibraryGroup } from '@/services/library';
import { useAppDispatch } from '@/store';
import { changeId } from '@/store/modules/library';

import { UploadFileProps } from '../rightPanel/library';
import styles from './index.module.less';
import LeftPanelItem from './item';

const LeftPanel: React.FC<{
  disabled: boolean;
  storyId: string | null;
  searchValue: LibraryObjectData;
  list: {
    id: string;
    name: string;
    disableOperate: boolean;
    keyIndex: string;
  }[];
  loading: boolean;
  onChangeSearch: (params: {
    searchValue: LibraryObjectData;
    limit?: UploadFileProps['limit'];
  }) => void
  dispatchLeftList: (value: {
    type: libraryReducerActions;
    payload?: {
      index?: number;
      keyIndex?: string;
      value?: any;
      append?: boolean;
      batchDelete?: {
        keyIndexs: string[];
      };
    };
  }) => void
}> = (({ searchValue, list = [], loading, dispatchLeftList, onChangeSearch, disabled, storyId = '' }) => {
  const dispatch = useAppDispatch();

  const handleAddItem = async () => {
    const materialId = (() => {
      switch (searchValue?.tabType) {
        case ResourceTabType.TAB_TYPE_NOVA:
          return PUBLIC;
        case ResourceTabType.TAB_TYPE_PIC:
          return PIC_PRIVATE;
        default:
          return storyId as string;
      }
    })();
    const defaultGroupItem = {
      name: '未命名',
      materialId,
    };
    const { code, message, data } = await insertLibraryGroup(defaultGroupItem);
    if (code === CgiErrorCode.NO_ERROR && data?.id) {
      dispatchLeftList({
        type: libraryReducerActions.ADD_ITEM,
        payload: {
          index: list?.length + 1,
          value: {
            id: data?.id,
            name: defaultGroupItem.name,
            disableOperate: false,
            keyIndex: undefined,
          },
        },
      });
      MessagePlugin.success('分组创建成功');
    } else {
      MessagePlugin.error(message || '分组创建失败，请重试');
    }
  };

  const handleOpenEdit = (id: string) => {
    dispatch(changeId({ id }));
  };

  const handleChangeSearchValue = (e: React.MouseEvent, id: string, clear?: boolean) => {
    e.stopPropagation();
    onChangeSearch({
      searchValue: {
        ...searchValue,
        materialId: id,
        type: UserResourceType.RESOURCE_TYPE_IMAGE,
        resourceName: '',
      },
    });
    if (clear) {
      dispatch(changeId({ id: '' }));
    }
  };

  const confirmDelete = async (id: string) => {
    if (!id) {
      MessagePlugin.error('请选择要删除的资源');
      return;
    }
    const { code, message } = await deleteUserResourceCustomGroup({
      ids: [id],
    });

    if (code === CgiErrorCode.NO_ERROR) {
      const newList = list.filter(i => i.id !== id);
      const index = list.findIndex(i => i.id === id);
      const keyIndex = list[index]?.keyIndex;

      dispatchLeftList({
        type: libraryReducerActions.REMOVE_ITEM,
        payload: {
          index,
          keyIndex,
        },
      });
      onChangeSearch({
        searchValue: {
          ...searchValue,
          materialId: newList[0]?.id || searchValue.materialId,
          resourceName: '',
        },
      });
      MessagePlugin.success('删除成功');
      return;
    }
    const name = list.find(i => i.id === id)?.name;
    MessagePlugin.error(message || `【${name}】删除失败，请重试`);
    return;
  };

  const reNameCallback = (
    code: CgiErrorCode,
    info: {
      name: string, id: string
    },
    item: {
      id: string;
      name: string;
      disableOperate: boolean;
      keyIndex: string;
    },
  ) => {
    if (code === CgiErrorCode.NO_ERROR) {
      dispatchLeftList({
        type: libraryReducerActions.UPDATE_ITEM,
        payload: {
          value: {
            ...item,
            ...info,
            disableOperate: false,
          },
          keyIndex: item.keyIndex,
        },
      });
    }
  };

  return (
    <div className={classNames(styles.wrap, { [styles.loading]: loading })}>
      <div className={classNames(styles.content, { [styles.loading]: loading })}>
        {
          loading ? <Loading size='middle' /> : <>
            {
              list.map((item) => {
                const active = searchValue?.materialId === item?.id;

                return (
                  <LeftPanelItem
                    key={item.keyIndex}
                    disabled={disabled}
                    item={item}
                    reNameCallback={reNameCallback}
                    active={active}
                    handleOpenEdit={handleOpenEdit}
                    confirmDelete={confirmDelete}
                    handleChangeSearchValue={handleChangeSearchValue}
                  />
                );
              })
            }
          </>
        }
      </div>
      {
        loading || disabled ? null : <div className={styles.add} onClick={handleAddItem}>
          <Button theme="default" variant="outline" style={{ width: '100%' }}>
            <PlusIcon size={18} /> <span className={styles.text}>新建分组</span>
          </Button>
        </div>
      }
    </div>
  );
});

export default LeftPanel;
